import React from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin, Tag, Clock } from 'lucide-react';
import type { EventItem } from '../../types/shared/event';
import { useImageDisplay } from '../../hooks/useImageDisplay';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useSection } from '../../hooks/useSection';
import { resolveColor } from '../../utils/colorUtils';
import { getTranslatedCategory } from '../../utils/translationHelpers';
import { GiftIcon } from '../icons';

interface ListViewProps {
  events: EventItem[];
  onEventClick: (event: EventItem) => void;
  onCategorySelect: (category: string) => void;
  onTypeSelect: (type: string) => void;
  config: any;
  translations: Record<string, Record<string, string>>;
  categoryColors: Map<string, string>;
}

export const ListView: React.FC<ListViewProps> = ({
  events,
  onEventClick,
  onCategorySelect,
  onTypeSelect,
  config,
  translations,
  categoryColors
}) => {
  const { i18n } = useTranslation();
  const section = useSection();
  
  // Initialize analytics with section context
  const analytics = useAnalytics({
    sectionId: section.id,
    sectionName: section.names.fr,
    sectionType: 'event'
  });

  // Grouper les événements par date
  const eventsByDate = React.useMemo(() => {
    const groups = new Map<string, EventItem[]>();

    if (!events || !Array.isArray(events)) {
      return groups;
    }

    events.forEach(event => {
      if (!groups.has(event.date)) {
        groups.set(event.date, []);
      }
      groups.get(event.date)!.push(event);
    });

    return groups;
  }, [events]);

  const backgroundColor = resolveColor(config['sectionBack-color'], config, '#f9fafb');

  // Créer un composant séparé pour chaque événement
  const ListItemCard = React.memo(({ event }: { event: EventItem }) => {
    // Get image URL from image_metadata.url if available
    const imageUrl = event.image_metadata?.url || event.image;
    const { displayImage, shouldDisplay } = useImageDisplay(imageUrl, 'listCard');

    const handleEventClick = () => {
      analytics.trackItemAction(event.id, 'item_click', 'event', {
        item_name: event.name,
        category: event.category,
        type: event.type,
        source: 'list_card',
        date: event.date,
        time: event.hour,
        show_id: event.idShow || event.idshow
      });
      
      onEventClick(event);
    };

    const handleCategoryClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      analytics.trackFilter('category_filter', event.category);
      onCategorySelect(event.category);
    };

    const handleTypeClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      analytics.trackFilter('type_filter', event.type);
      onTypeSelect(event.type);
    };

    return (
      <div
        className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-200 relative"
        style={{ borderRadius: config.border_radius }}
      >
        {/* Gift icon for items with offers */}
        {event.offer && (
          <div 
            className="absolute top-0 right-0 w-10 h-10 flex items-center justify-center z-10"
            style={{ 
              backgroundColor: config['major-color'],
              borderTopRightRadius: config.border_radius,
              borderBottomLeftRadius: config.border_radius
            }}
          >
            <GiftIcon size={20} className="text-white" />
          </div>
        )}
        
        <div 
          className="cursor-pointer"
          onClick={handleEventClick}
        >
          {shouldDisplay && displayImage && (
            <div className="relative pb-[66.666%]">
              <img
                src={displayImage}
                alt={event.name}
                loading="lazy"
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
          )}
          
          <div className="p-3">
            <h3 className="text-lg font-medium mb-1">{event.name}</h3>
            
            <div className="flex items-center text-sm text-gray-600 mb-1">
              <MapPin size={16} className="mr-1 shrink-0" />
              <div className="flex items-center w-full">
                <span className="shrink-0 whitespace-nowrap">{event.distance}km</span>
                <span className="mx-1 shrink-0">•</span>
                <span className="truncate max-w-[calc(100%-60px)] sm:max-w-[150px] md:max-w-[180px] lg:max-w-[200px] xl:max-w-[250px]">
                  {event.place}
                </span>
              </div>
            </div>

            {event.artiste && (
              <p className="text-sm text-gray-600 mb-1">{event.artiste}</p>
            )}

            <div className="flex flex-wrap items-center gap-2 text-sm mb-2">
              {event.hour && (
                <div className="flex items-center text-gray-600">
                  <Clock size={16} className="mr-1" />
                  <span>
                    {new Intl.DateTimeFormat(i18n.language, {
                      day: 'numeric',
                      month: 'short'
                    }).format(new Date(event.date))}
                    {' - '}
                    {new Intl.DateTimeFormat(i18n.language, {
                      hour: 'numeric',
                      minute: 'numeric',
                      hour12: i18n.language === 'en'
                    }).format(new Date(`2000-01-01T${event.hour}`))}
                  </span>
                </div>
              )}
              
              {event.price && (
                <div 
                  className="flex items-center"
                  style={{ color: config['major-color'] }}
                >
                  <Tag size={16} className="mr-1" />
                  <span>{event.price}</span>
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-1">
              <button
                type="button"
                onClick={handleCategoryClick}
                className="inline-block px-1.5 py-0.5 text-xs rounded hover:opacity-80 transition-opacity text-white"
                style={{ backgroundColor: categoryColors.get(event.category) || config['medium-color'] }}
              >
                {getTranslatedCategory(translations, event.category, i18n.language)}
              </button>
              
              {event.type && (
                <button
                  type="button"
                  onClick={handleTypeClick}
                  className="inline-block px-1.5 py-0.5 text-xs rounded hover:opacity-80 transition-opacity text-white opacity-70"
                  style={{ backgroundColor: config['medium-color'] }}
                >
                  {getTranslatedCategory(translations, event.type, i18n.language)}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  });

  if (!events || events.length === 0) {
    return (
      <div 
        className="flex items-center justify-center min-h-screen"
        style={{ backgroundColor }}
      >
        <p className="text-gray-500">Aucun événement disponible</p>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen pb-20"
      style={{ backgroundColor }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {Array.from(eventsByDate.entries()).map(([date, dateEvents]) => (
          <div key={date} className="mb-8">
            <h2 
              className="text-xl font-semibold mb-4"
              style={{ color: config['dark-color'] }}
            >
              {new Intl.DateTimeFormat(i18n.language, {
                weekday: 'long',
                day: 'numeric',
                month: 'long'
              }).format(new Date(date))}
            </h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {dateEvents.map((event) => (
                <ListItemCard key={event.id} event={event} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};